import { Formik, type FormikHelpers, useFormikContext } from "formik";
import { Calendar } from "lucide-react";
import { useId } from "react";

import { CreatePatientSex } from "@elation/api-client/src/stitch-web/models/createPatientSex";

import { Alert, AlertDescription } from "@src/shared/components/ui/alert";
import { Button } from "@src/shared/components/ui/button";
import { Calendar as DatePickerCalendar } from "@src/shared/components/ui/calendar";
import { Input } from "@src/shared/components/ui/input";
import { Label } from "@src/shared/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@src/shared/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@src/shared/components/ui/select";
import { Spinner } from "@src/shared/components/ui/spinner";
import { cn } from "@src/shared/lib/utils";
import { NaiveDate } from "@src/shared/utils/types";

import {
  CreatePatientFormInitialValues,
  CreatePatientFormValidationSchema,
  type CreatePatientFormValues,
} from "../../../../domain";

export interface CreatePatientFormProps {
  cancelLabel?: string;
  className?: string;
  error?: string | null;
  footerClassName?: string;
  initialValues?: Partial<CreatePatientFormValues>;
  isSubmitting?: boolean;
  onCancel: () => void;
  onSubmit: (values: CreatePatientFormValues) => Promise<void> | void;
  submitLabel?: string;
}

const sexOptions = [
  { label: "Female", value: CreatePatientSex.Female },
  { label: "Male", value: CreatePatientSex.Male },
  { label: "Other", value: CreatePatientSex.Other },
  { label: "Unknown", value: CreatePatientSex.Unknown },
];

function dateOnlyStringToLocalDate(value: string): Date | undefined {
  const [year, month, day] = value.split("-").map(Number);

  if (!year || !month || !day) return undefined;

  const date = new Date(year, month - 1, day);
  return Number.isNaN(date.getTime()) ? undefined : date;
}

function formDateToLocalDate(value: string): Date | undefined {
  if (!value) return undefined;

  try {
    return dateOnlyStringToLocalDate(
      NaiveDate.toString(NaiveDate.fromString(value)),
    );
  } catch {
    return undefined;
  }
}

function localDateToDateOnlyString(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function getInitialValues(
  initialValues?: Partial<CreatePatientFormValues>,
): CreatePatientFormValues {
  return {
    ...CreatePatientFormInitialValues,
    ...initialValues,
  };
}

export function CreatePatientFormV2({
  cancelLabel = "Cancel",
  className,
  error = null,
  footerClassName,
  initialValues,
  isSubmitting = false,
  onCancel,
  onSubmit,
  submitLabel = "Create patient",
}: CreatePatientFormProps) {
  const handleSubmit = async (
    values: CreatePatientFormValues,
    helpers: FormikHelpers<CreatePatientFormValues>,
  ) => {
    try {
      await onSubmit(values);
      helpers.resetForm();
    } catch {
      helpers.setSubmitting(false);
    }
  };

  return (
    <Formik<CreatePatientFormValues>
      initialValues={getInitialValues(initialValues)}
      validationSchema={CreatePatientFormValidationSchema}
      onSubmit={handleSubmit}
      validateOnBlur={false}
      validateOnChange={false}
      enableReinitialize
    >
      <CreatePatientFormContent
        cancelLabel={cancelLabel}
        className={className}
        error={error}
        footerClassName={footerClassName}
        isSubmitting={isSubmitting}
        onCancel={onCancel}
        submitLabel={submitLabel}
      />
    </Formik>
  );
}

interface CreatePatientFormContentProps {
  cancelLabel: string;
  className?: string;
  error: string | null;
  footerClassName?: string;
  isSubmitting: boolean;
  onCancel: () => void;
  submitLabel: string;
}

function CreatePatientFormContent({
  cancelLabel,
  className,
  error,
  footerClassName,
  isSubmitting,
  onCancel,
  submitLabel,
}: CreatePatientFormContentProps) {
  const generatedId = useId();
  const errorId = `${generatedId}-error`;
  const firstNameId = `${generatedId}-first-name`;
  const lastNameId = `${generatedId}-last-name`;
  const dobId = `${generatedId}-dob`;
  const sexId = `${generatedId}-sex`;
  const {
    errors,
    handleBlur,
    handleChange,
    handleSubmit,
    isSubmitting: isFormikSubmitting,
    resetForm,
    setFieldValue,
    values,
  } = useFormikContext<CreatePatientFormValues>();
  const submitting = isSubmitting || isFormikSubmitting;
  const fieldClassName =
    "h-9 rounded-[10px] px-3 text-[13px] font-semibold text-surface-foreground placeholder:text-muted-foreground";
  const selectTriggerClassName =
    "h-9 rounded-[10px] px-3 text-[13px] font-semibold text-surface-foreground data-[placeholder]:text-muted-foreground [&>svg]:size-3.5";
  const labelClassName =
    "text-[13px] font-semibold leading-none text-muted-foreground";
  const buttonClassName = "h-8 rounded-[10px] px-4 text-[13px] font-bold";
  const selectedDob = formDateToLocalDate(values.dob);

  const handleCancel = () => {
    resetForm();
    onCancel();
  };

  return (
    <form
      onSubmit={handleSubmit}
      className={cn("grid gap-2.5", className)}
      noValidate
    >
      {error ? (
        <Alert
          variant="destructive"
          aria-live="polite"
          aria-atomic="true"
          id={errorId}
        >
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : null}

      <div className="grid grid-cols-2 gap-x-3 gap-y-4">
        <div className="grid gap-2">
          <Label htmlFor={firstNameId} className={labelClassName}>
            First name
          </Label>
          <Input
            id={firstNameId}
            name="firstName"
            value={values.firstName}
            autoComplete="given-name"
            className={fieldClassName}
            aria-invalid={Boolean(errors.firstName)}
            aria-describedby={
              errors.firstName ? `${firstNameId}-error` : undefined
            }
            disabled={submitting}
            onBlur={handleBlur}
            onChange={handleChange}
          />
          <p
            id={`${firstNameId}-error`}
            className="min-h-4 text-xs text-destructive"
            aria-live="polite"
          >
            {errors.firstName}
          </p>
        </div>

        <div className="grid gap-2">
          <Label htmlFor={lastNameId} className={labelClassName}>
            Last name
          </Label>
          <Input
            id={lastNameId}
            name="lastName"
            value={values.lastName}
            autoComplete="family-name"
            className={fieldClassName}
            aria-invalid={Boolean(errors.lastName)}
            aria-describedby={
              errors.lastName ? `${lastNameId}-error` : undefined
            }
            disabled={submitting}
            onBlur={handleBlur}
            onChange={handleChange}
          />
          <p
            id={`${lastNameId}-error`}
            className="min-h-4 text-xs text-destructive"
            aria-live="polite"
          >
            {errors.lastName}
          </p>
        </div>

        <div className="grid gap-2">
          <Label htmlFor={dobId} className={labelClassName}>
            Date of birth
          </Label>
          <div className="relative">
            <Input
              id={dobId}
              name="dob"
              value={values.dob}
              placeholder="mm/dd/yyyy"
              autoComplete="bday"
              className={cn(fieldClassName, "pr-9")}
              aria-invalid={Boolean(errors.dob)}
              aria-describedby={errors.dob ? `${dobId}-error` : undefined}
              disabled={submitting}
              onBlur={handleBlur}
              onChange={(event) =>
                setFieldValue("dob", event.target.value, true)
              }
            />
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-1 top-1/2 size-7 -translate-y-1/2 rounded-[8px] text-muted-foreground hover:bg-transparent hover:text-surface-foreground"
                  aria-label="Open date picker"
                  disabled={submitting}
                >
                  <Calendar className="size-3.5" aria-hidden="true" />
                </Button>
              </PopoverTrigger>
              <PopoverContent
                className="w-auto p-0"
                align="end"
                side="bottom"
                sideOffset={6}
                avoidCollisions={false}
              >
                <DatePickerCalendar
                  mode="single"
                  selected={selectedDob}
                  onSelect={(date) => {
                    if (!date) return;
                    setFieldValue("dob", localDateToDateOnlyString(date), true);
                  }}
                  disabled={(date) => date > new Date()}
                />
              </PopoverContent>
            </Popover>
          </div>
          <p
            id={`${dobId}-error`}
            className="min-h-4 text-xs text-destructive"
            aria-live="polite"
          >
            {errors.dob}
          </p>
        </div>

        <div className="grid gap-2">
          <Label htmlFor={sexId} className={labelClassName}>
            Sex at birth
          </Label>
          <Select
            value={values.sex}
            disabled={submitting}
            onValueChange={(value) => setFieldValue("sex", value, true)}
          >
            <SelectTrigger
              id={sexId}
              className={selectTriggerClassName}
              aria-invalid={Boolean(errors.sex)}
              aria-describedby={errors.sex ? `${sexId}-error` : undefined}
            >
              <SelectValue placeholder="Select" />
            </SelectTrigger>
            <SelectContent>
              {sexOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p
            id={`${sexId}-error`}
            className="min-h-4 text-xs text-destructive"
            aria-live="polite"
          >
            {errors.sex}
          </p>
        </div>
      </div>

      <div
        className={cn("flex flex-row justify-end gap-3 pt-2", footerClassName)}
      >
        <Button
          type="button"
          variant="outline"
          className={buttonClassName}
          disabled={submitting}
          onClick={handleCancel}
        >
          {cancelLabel}
        </Button>
        <Button type="submit" className={buttonClassName} disabled={submitting}>
          {submitting ? <Spinner className="mr-1" /> : null}
          {submitLabel}
        </Button>
      </div>
    </form>
  );
}
